import { useEffect, useContext, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { SessionContext } from "../context/SessionContext";
import { ProfileContext } from "../context/ProfileContext";
import { toast } from "react-hot-toast";
import { format } from "date-fns";

import Select from "react-select";
import ToastDisplay from "../components/ToastDisplay";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Modal from "react-bootstrap/Modal";
import Spinner from "react-bootstrap/Spinner";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Pagination from "../components/Pagination";
import PageLoader from "../components/PageLoader";
import Empty from "../components/Empty";

const Announcements = () => {
	const navigate = useNavigate();
	const photoRef = useRef(null);
	const [session] = useContext(SessionContext);
	const [profile] = useContext(ProfileContext);
	const [messages, setMessages] = useState([]);
	const [meta, setMeta] = useState(null);
	const [searchParams, setSearchParams] = useSearchParams({});
	const [loading, setLoading] = useState(true);
	const [actionLoading, setActionLoading] = useState(false);
	const [filtermodal, setFilterModal] = useState(false);
	const [showFilters, setShowFilters] = useState(false);
	const [messageModal, setMessageModal] = useState(false);
	const [errors, setErrors] = useState([]);
	const [departments, setDepartments] = useState([]);
	const [levels, setLevels] = useState([]);
	const [segments, setSegments] = useState(1);

	const [recipient, setRecipient] = useState("");
	const [recipientFilters, setRecipientFilters] = useState([]);
	const [content, setContent] = useState("");

	const [searchQ, setSearchQ] = useState(searchParams.get("q") || "");
	const [searchSender, setSearchSender] = useState(
		searchParams.get("sender") || ""
	);
	const [searchDate, setSearchDate] = useState(searchParams.get("date") || "");
	const [searchRecipient, setSearchRecipient] = useState(
		searchParams.get("recipient") || ""
	);

	const searchFields = [
		{ key: "q", value: searchQ },
		{ key: "sender", value: searchSender },
		{ key: "date", value: searchDate },
		{ key: "recipient", value: searchRecipient },
	];

	const filterFields = [
		{
			key: "sender",
			value: searchSender,
			defaultVal: "",
			edit: setSearchSender,
		},
		{
			key: "date",
			value: searchDate,
			defaultVal: "",
			edit: setSearchDate,
		},
		{
			key: "recipient",
			value: searchRecipient,
			defaultVal: "",
			edit: setSearchRecipient,
		},
	];

	const requiredFields = [
		{ key: "recipient", value: recipient },
		{ key: "content", value: content },
	];

	const getData = async () => {
		setLoading(true);
		const requestOptions = {
			method: "GET",
			headers: { "X-Auth-Token": session },
		};

		const response = await fetch(
			`${process.env.REACT_APP_API_URI}/announcements`,
			requestOptions
		);
		const data = await response.json();

		if (!response.ok) {
			toast(<ToastDisplay status="error" content="Something went wrong." />);
		} else {
			setMessages(data.announcements);
			setMeta(data.meta);
			setDepartments(
				data.departments.map((dept) => ({
					value: dept.name,
					label: dept.name,
				}))
			);
			setLevels(
				data.levels.map((level) => ({
					value: level.name,
					label: level.name,
				}))
			);
		}
		setLoading(false);
	};

	const search = (e) => {
		e.preventDefault();
		var newSearch = { ...searchParams };
		searchFields.map((field) => {
			if (field.value !== "") {
				newSearch[field.key] = field.value;
			} else {
				delete newSearch[field.key];
			}
		});
		setSearchParams(newSearch);
		setFilterModal(false);
	};

	const removeFilter = (e, func, key) => {
		e.preventDefault();
		func("");
		var newSearch = { ...searchParams };
		searchFields.map((field) => {
			if (field.value !== "" && field.key !== key) {
				newSearch[field.key] = field.value;
			} else {
				delete newSearch[field.key];
			}
		});
		setSearchParams(newSearch);
	};

	const clearFilters = (e) => {
		e.preventDefault();
		setSearchParams({});
		filterFields.map((field) => {
			field.edit(field.defaultVal);
		});
	};

	const closeMessageModal = () => {
		setMessageModal(false);
		setRecipient("");
		setRecipientFilters([]);
		setContent("");
		setErrors([]);
	};

	const isEmpty = (value) => {
		return value === "" || value === null || value === undefined;
	};

	const sendMessage = async () => {
		const requestOptions = {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				"X-Auth-Token": session,
			},
			body: JSON.stringify({
				recipient: recipient,
				filters: recipientFilters?.map((filter) => filter.value),
				content: content,
				segments: segments,
			}),
		};

		const response = await fetch(
			`${process.env.REACT_APP_API_URI}/announcements/`,
			requestOptions
		);
		const data = await response.json();

		if (!response.ok) {
			toast(<ToastDisplay status="error" content={data.msg} />);
		} else {
			toast(<ToastDisplay status="success" content={data.msg} />);
			closeMessageModal();
			navigate(`/announcements/${data.announcement_id}`);
		}
		setActionLoading(false);
	};

	const handleSubmit = (e) => {
		e.preventDefault();
		setActionLoading(true);
		var newErrors = [];

		requiredFields.map((field) => {
			if (!field.value || field.value === "" || field.value.length === 0) {
				newErrors.push(field.key);
			}
		});

		if (newErrors.length !== 0) {
			toast(
				<ToastDisplay
					status="error"
					content="Please fill out all required fields."
				/>
			);
			setActionLoading(false);
			setErrors(newErrors);
			return;
		}
		sendMessage();
	};

	const isUnicode = (text) => {
		// GSM-7 character set (basic subset)
		const gsmRegex =
			/^[\x00-\x7F€£¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ!"#¤%&'()*+,\-./0-9:;<=>?@A-ZÄÖÑÜ§a-zäöñüà^{}\[~\]|€`]*$/;
		return !gsmRegex.test(text);
	};

	const calculateSegments = (text) => {
		if (!text) return 0;
		const unicode = isUnicode(text);
		const limit = unicode ? 70 : 160;
		const perSegment = unicode ? 67 : 153;

		if (text.length <= limit) return 1;
		return Math.ceil(text.length / perSegment);
	};

	useEffect(() => {
		setSegments(calculateSegments(content));
	}, [content]);

	useEffect(() => {
		document.title = "Announcements | Pisara Dashboard";
		if (profile) {
			if (!profile.role.announcements_view) {
				navigate("/");
			} else {
				getData();
				if (filterFields.every((field) => isEmpty(field.value))) {
					setShowFilters(false);
				} else {
					setShowFilters(true);
				}
			}
		}
	}, [searchParams, profile]);

	return (
		<>
			<Container className="gx-5">
				<Row className="content-header flex-row-left mb-20">
					<Col>
						<h1 className="text-xl m-0">Announcements</h1>
					</Col>
				</Row>
				<Row className="flex-row-left mb-20">
					<Col className="col-5">
						<Form onSubmit={search}>
							<div className="search-container bg-white flex-row-left">
								<i className="ri-search-line search-icon" />
								<Form.Control
									className="text-sm"
									placeholder="Search messages"
									value={searchQ}
									onChange={(e) => setSearchQ(e.target.value)}
								/>
								<Button
									variant="light"
									className="filter-btn flex-column"
									onClick={() => setFilterModal(true)}
								>
									<i className="ri-equalizer-3-fill m-0" />
								</Button>
							</div>
						</Form>
					</Col>
					<Col className="col-7 flex-row-right">
						<Pagination
							meta={meta}
							searchParams={searchParams}
							setSearchParams={setSearchParams}
							searchFields={searchFields}
							className="pull-right flex-row-left"
						/>
						<Button
							variant="light"
							className={`refresh-btn flex-column ${
								!profile?.role?.announcements_write && "no-margin"
							}`}
							onClick={getData}
						>
							<i className="ri-restart-line opacity-7" />
						</Button>
						{profile?.role?.announcements_write && (
							<Button
								className="control-btn btn-sm btn-default text-medium-gotham text-sm"
								onClick={() => setMessageModal(true)}
							>
								<i className="ri-send-plane-line" />
								New Message
							</Button>
						)}
					</Col>
				</Row>
				<Row className="content-table-header bg-white gx-0">
					<Col className="p-10 col-2">
						<span className="text-sm text-medium-gotham mb-10 block">Date</span>
					</Col>
					<Col className="p-10 col-2">
						<span className="text-sm text-medium-gotham mb-10 block">To</span>
					</Col>
					<Col className="p-10 col-8">
						<span className="text-sm text-medium-gotham mb-10 block">
							Message
						</span>
					</Col>
				</Row>
				{loading ? (
					<PageLoader className="bg-white p-50" />
				) : messages?.length === 0 ? (
					<Empty label="No messages to show." className="bg-white" />
				) : (
					messages.map((message) => (
						<Link key={message.id} to={`/announcements/${message.id}`}>
							<Row className="content-table-item bg-white gx-0">
								<Col className="p-10 col-2 ellipsis">
									<span className="text-sm">
										{format(new Date(message.created_at), "PP")}
									</span>
								</Col>
								<Col className="p-10 col-2 ellipsis">
									<span className="text-sm">{message.recipient}</span>
								</Col>
								<Col className="p-10 col-8 ellipsis">
									<span className="text-sm">{message.content}</span>
								</Col>
							</Row>
						</Link>
					))
				)}
			</Container>
			<Modal
				show={messageModal}
				className="admin-content"
				centered
				size="lg"
				onHide={closeMessageModal}
			>
				<Form onSubmit={handleSubmit}>
					<Modal.Header>
						<Modal.Title className="text-lg">New Message</Modal.Title>
					</Modal.Header>
					<Modal.Body>
						<Row className="mb-30">
							<Col>
								<Form.Label className="text-sm">To</Form.Label>
								<Form.Select
									className={`text-sm form-input ${
										errors.includes("recipient") && "border-danger"
									}`}
									value={recipient}
									onChange={(e) => setRecipient(e.target.value)}
								>
									<option value=""></option>
									<option value="Parents">Parents</option>
									<option value="Personnel">Personnel</option>
								</Form.Select>
							</Col>
						</Row>
						{recipient && recipient !== "All" && (
							<Row className="mb-30">
								<Col>
									<Form.Label className="text-sm">
										{recipient === "Parents" ? "Levels" : "Departments"}{" "}
										(Optional)
									</Form.Label>
									<Select
										isMulti
										value={recipientFilters}
										onChange={setRecipientFilters}
										options={recipient === "Parents" ? levels : departments}
										menuShouldScrollIntoView={false}
										maxMenuHeight={200}
										placeholder=""
										styles={{
											control: (baseStyles, state) => ({
												...baseStyles,
												height: "38px",
												outline: "none",
												borderRadius: 4,
												fontSize: "0.875rem",
												borderColor: state.isFocused
													? "#86b7fe !important"
													: "#dee2e6 !important",
												boxShadow: state.isFocused
													? "0 0 0 .25rem rgba(13, 110, 253, .25)"
													: "none",
											}),
											option: (baseStyles, state) => ({
												...baseStyles,
												fontSize: ".875rem",
												color: "#050911",
												backgroundColor: state.isFocused
													? "#c8deff !important"
													: "#fff",
											}),
										}}
									/>
								</Col>
							</Row>
						)}
						<Row className="mb-30">
							<Col>
								<Form.Label className="text-sm">Message</Form.Label>
								<Form.Control
									as="textarea"
									className={`text-sm form-input ${
										errors.includes("content") && "border-danger"
									}`}
									value={content}
									onChange={(e) => setContent(e.target.value)}
								/>
							</Col>
						</Row>
						<div className="flex-row-left mb-10">
							<span className="text-medium-gotham text-sm me-1">
								Characters:
							</span>
							<span className="text-sm text-regular-gotham">
								{content.length}
							</span>
						</div>
						<div className="flex-row-left mb-10">
							<span className="text-medium-gotham text-sm me-1">
								Segments/Parts:
							</span>
							<span className="text-sm text-regular-gotham">{segments}</span>
						</div>
					</Modal.Body>
					<Modal.Footer>
						<Button
							variant="outline-secondary"
							className="btn-sm action-btn text-sm text-medium-inter me-2"
							onClick={closeMessageModal}
						>
							Cancel
						</Button>
						<Button
							type="submit"
							className="btn-default btn-sm action-btn flex-column text-sm text-medium-inter"
							disabled={actionLoading}
						>
							{actionLoading ? (
								<Spinner className="btn-loader text-white" />
							) : (
								"Send"
							)}
						</Button>
					</Modal.Footer>
				</Form>
			</Modal>
		</>
	);
};

export default Announcements;
